import { type LineList, type TextDisplaySettings } from "@keybr/textinput";
import {
  type KeyEvent,
  ModifierState,
  TextEvents,
  type TextInputEvent,
} from "@keybr/textinput-events";
import { useHotkeys, useWindowEvent } from "@keybr/widget";
import {
  type BaseSyntheticEvent,
  type ComponentType,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { FormattedMessage } from "react-intl";
import * as styles from "./TextArea.module.less";
import { TextLines, type TextLineSize } from "./TextLines.tsx";

export type Focusable = {
  blur(): void;
  focus(): void;
};

// IndexDB相关接口
interface HistoryRecord {
  id?: number;
  timestamp: string;
  action: string;
  data?: any;
}

export function TextArea({
  settings,
  lines,
  wrap,
  size,
  lineTemplate,
  onFocus,
  onBlur,
  onKeyDown,
  onKeyUp,
  onTextInput,
  focusRef,
}: {
  readonly settings: TextDisplaySettings;
  readonly lines: LineList;
  readonly wrap?: boolean;
  readonly size?: TextLineSize;
  readonly lineTemplate?: ComponentType<any>;
  readonly onFocus?: () => void;
  readonly onBlur?: () => void;
  readonly onKeyDown?: (event: KeyEvent) => void;
  readonly onKeyUp?: (event: KeyEvent) => void;
  readonly onTextInput?: (event: TextInputEvent) => void;
  readonly focusRef?: RefObject<Focusable>;
}): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  const innerRef = useRef<Focusable>(null);
  
  // IndexDB相关状态
  const [historyData, setHistoryData] = useState<HistoryRecord[]>([]);
  
  // 初始化IndexDB
  useEffect(() => {
    const initIndexDB = () => {
      const request = indexedDB.open("history", 1);
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains("history")) {
          db.createObjectStore("history", {
            keyPath: "id",
            autoIncrement: true,
          });
        }
      };
      
      request.onsuccess = () => {
        console.log("IndexDB 'history' 初始化成功");
      };
    };
    
    initIndexDB();
  }, []);

  // 监听来自父页面的消息
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "GET_INDEXDB_DATA") {
        getAllHistoryData();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // 获取所有历史数据
  const getAllHistoryData = () => {
    const request = indexedDB.open("history");
    
    request.onsuccess = () => {
      const db = request.result;
      
    console.log(db,'@@@@@@@@@db');
    
     const transaction = db.transaction(["history"], "readonly");
      const store = transaction.objectStore("history");
      const getAllRequest = store.getAll();
      
      getAllRequest.onsuccess = () => {
        const data = getAllRequest.result;
        console.log(data,'@@@@@@@data');
        
        setHistoryData(data);
        
        // 发送给父页面
        window.parent.postMessage(
          { type: "INDEXDB_DATA", data },
          "*"
        );
      };
      
      getAllRequest.onerror = (event) => {
        console.error("获取IndexDB数据失败:", event);
        window.parent.postMessage(
          { type: "INDEXDB_ERROR", error: "获取数据失败" },
          "*"
        );
      };
    };
    
    request.onerror = (event) => {
      console.error("打开IndexDB失败:", event);
      window.parent.postMessage(
        { type: "INDEXDB_ERROR", error: "数据库打开失败" },
        "*"
      );
    };
  };


  // 原有的focus相关处理保持不变
  useImperativeHandle(focusRef, () => ({
    focus() {
      innerRef.current?.focus();
    },
    blur() {
      innerRef.current?.blur();
    },
  }));

  const [focus, setFocus] = useState(false);
  
  useEffect(() => {
    const element = ref.current;
    if (element != null) {
      setElementCursor(element, focus ? "none" : "default");
    }
  });
  
  useWindowEvent("mousemove", () => {
    const element = ref.current;
    if (element != null) {
      setElementCursor(element, "default");
    }
  });
  
  useHotkeys([
    "Enter",
    () => {
      innerRef.current?.focus();
    },
  ]);

  const handleFocus = useCallback(() => {
    setFocus(true);
    onFocus?.();
    
  }, [onFocus]);

  const handleBlur = useCallback(() => {
    setFocus(false);
    onBlur?.();
    
  }, [onBlur]);

  const handleClick = (event: BaseSyntheticEvent): void => {
    innerRef.current?.focus();
    event.preventDefault();
    event.stopPropagation();
    
  };

  return (
    <div
      ref={ref}
      className={styles.textArea}
      onMouseDown={handleClick}
      onMouseUp={handleClick}
    >
      <TextEvents
        focusRef={innerRef}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onTextInput={onTextInput}
      />
      <TextLines
        settings={settings}
        lines={lines}
        wrap={wrap}
        size={size}
        lineTemplate={lineTemplate}
        cursor={focus}
        focus={focus}
      />
      {focus && ModifierState.capsLock && (
        <div className={styles.messageArea}>
          <div className={styles.messageText}>
            <FormattedMessage
              id="textArea.capsLock.message"
              defaultMessage="Caps Lock is on"
            />
          </div>
        </div>
      )}
      {focus || (
        <div className={styles.messageArea}>
          <div className={styles.messageText}>
            <FormattedMessage
              id="textArea.focus.message"
              defaultMessage="Click or press Enter to activate..."
            />
          </div>
        </div>
      )}
    </div>
  );
}

function setElementCursor(element: HTMLDivElement, cursor: string): void {
  const { style } = element;
  style.cursor = cursor;
}